'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Language, TRANSLATIONS } from '@/lib/i18n';

const STORAGE_KEY = 'juga-language';
const SEARCH_PARAM_KEY = 'lang';

interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  dictionary: (typeof TRANSLATIONS)[Language];
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function resolveBrowserLanguage(): Language {
  if (typeof window === 'undefined') {
    return 'es';
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'es' || stored === 'en') {
    return stored;
  }

  const browserLanguage = window.navigator.language.toLowerCase();
  return browserLanguage.startsWith('en') ? 'en' : 'es';
}

function resolveQueryLanguage(): Language | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const lang = params.get(SEARCH_PARAM_KEY);
  if (lang === 'es' || lang === 'en') {
    return lang;
  }

  return null;
}

export default function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('es');

  useEffect(() => {
    const nextLanguage = resolveQueryLanguage() ?? resolveBrowserLanguage();
    setLanguageState(nextLanguage);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;

    const url = new URL(window.location.href);
    const currentLanguage = url.searchParams.get(SEARCH_PARAM_KEY);

    if (language === 'es') {
      url.searchParams.delete(SEARCH_PARAM_KEY);
    } else {
      url.searchParams.set(SEARCH_PARAM_KEY, language);
    }

    const nextRelativeUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentRelativeUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextRelativeUrl !== currentRelativeUrl && currentLanguage !== language) {
      window.history.replaceState(null, '', nextRelativeUrl);
    }
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: setLanguageState,
      dictionary: TRANSLATIONS[language],
    }),
    [language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider.');
  }

  return context;
}
